import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { stylistAPI } from '../../services/api';
import StylistLayout from '../../components/StylistLayout';
import { 
  Loader2, 
  Plus, 
  Search,
  User,
  Shirt,
  Link as LinkIcon,
  Copy,
  Check,
  X,
  MessageSquare,
  Clock,
  CheckCircle,
  AlertCircle,
  ChevronRight
} from 'lucide-react';

const StylistDashboard = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [magicLinkModal, setMagicLinkModal] = useState(null);
  const [copied, setCopied] = useState(false);
  const [requestsStats, setRequestsStats] = useState(null);
  const [showNewRequestsList, setShowNewRequestsList] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
    fetchRequestsStats();
  }, []);

  const fetchClients = async () => {
    try {
      const data = await stylistAPI.getAllClients();
      setClients(data);
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRequestsStats = async () => {
    try {
      const data = await stylistAPI.getRequestsStats();
      setRequestsStats(data);
    } catch (err) {
      console.error('Error fetching requests stats:', err);
    }
  };

  const handleGenerateMagicLink = async (clientId, clientName, e) => {
    e.stopPropagation();
    try {
      const response = await stylistAPI.generateMagicLink(clientId);
      const fullUrl = `${window.location.origin}${response.magic_link}`;
      setMagicLinkModal({
        clientName,
        link: fullUrl,
        expires: response.expires
      });
    } catch (err) {
      console.error('Error generating magic link:', err);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(magicLinkModal.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error copying:', err);
    }
  };

  // New Requests List Modal (inline)
  const NewRequestsListModal = () => (
    <div
      onClick={() => setShowNewRequestsList(false)}
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-l)',
        zIndex: 1000
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--white)',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}
      >
        <div style={{
          padding: 'var(--space-l)',
          borderBottom: '1px solid var(--soft-gray)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          backgroundColor: 'var(--white)'
        }}>
          <h3 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.25rem',
            fontWeight: 400,
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-s)'
          }}>
            <AlertCircle size={20} style={{ color: '#f59e0b' }} />
            Новые запросы
          </h3>
          <button
            onClick={() => setShowNewRequestsList(false)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
          >
            <X size={20} />
          </button>
        </div>
        <div style={{ padding: 'var(--space-l)' }}>
          {requestsStats?.new_requests?.length === 0 ? (
            <p style={{ fontFamily: 'var(--font-body)', color: 'var(--mid-gray)', textAlign: 'center' }}>
              Нет новых запросов
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-m)' }}>
              {requestsStats?.new_requests?.map((req) => (
                <div
                  key={req.id}
                  onClick={() => {
                    setShowNewRequestsList(false);
                    navigate(`/stylist/clients/${req.client_id}`);
                  }}
                  style={{
                    padding: 'var(--space-m)',
                    border: '1px solid var(--soft-gray)',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: 'var(--space-s)'
                  }}>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.9375rem', fontWeight: 500 }}>
                      {req.client_name}
                    </span>
                    <span style={{
                      padding: '4px 8px',
                      backgroundColor: 'var(--cream)',
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.75rem'
                    }}>
                      {req.request_type === 'look' ? 'Образ' : 'Покупка'}
                    </span>
                  </div>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
                    {req.description}
                  </p>
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.75rem', color: 'var(--light-gray)' }}>
                    {new Date(req.created_at).toLocaleString('ru-RU')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.telegram_username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <StylistLayout>
      <div style={{ maxWidth: '1200px' }} data-testid="stylist-dashboard">
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 'var(--space-xl)',
          flexWrap: 'wrap',
          gap: 'var(--space-m)'
        }}>
          <div>
            <h1 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(2rem, 4vw, 2.5rem)',
              fontWeight: 300,
              marginBottom: 'var(--space-s)'
            }}>
              Клиенты
            </h1>
            <p style={{
              fontFamily: 'var(--font-body)',
              color: 'var(--mid-gray)',
              fontSize: '0.875rem'
            }}>
              {clients.length} {clients.length === 1 ? 'клиент' : clients.length < 5 ? 'клиента' : 'клиентов'}
            </p>
          </div>

          <button
            onClick={() => setShowNewClientModal(true)}
            data-testid="add-client-btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-s)',
              padding: 'var(--space-m) var(--space-l)',
              backgroundColor: 'var(--black)',
              color: 'var(--white)',
              border: 'none',
              fontFamily: 'var(--font-body)',
              fontSize: '0.8125rem',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              cursor: 'pointer'
            }}
          >
            <Plus size={18} />
            Новый клиент
          </button>
        </div>

        {/* Requests Stats Widget */}
        {requestsStats && requestsStats.stats.total > 0 && (
          <div style={{
            marginBottom: 'var(--space-xl)',
            padding: 'var(--space-l)',
            backgroundColor: 'var(--white)',
            border: '1px solid var(--soft-gray)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 'var(--space-m)'
            }}>
              <h3 style={{
                fontFamily: 'var(--font-display)',
                fontSize: '1.125rem',
                fontWeight: 400,
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-s)'
              }}>
                <MessageSquare size={20} />
                Запросы клиентов
              </h3>
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-xl)' }}>
              <div
                onClick={() => requestsStats.stats.new > 0 && setShowNewRequestsList(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-s)',
                  padding: 'var(--space-m)',
                  backgroundColor: requestsStats.stats.new > 0 ? '#f59e0b15' : 'var(--cream)',
                  cursor: requestsStats.stats.new > 0 ? 'pointer' : 'default',
                  transition: 'all 200ms ease'
                }}
                data-testid="new-requests-stat"
              >
                <AlertCircle size={18} style={{ color: '#f59e0b' }} />
                <div>
                  <p style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: '1.5rem',
                    fontWeight: 500,
                    color: requestsStats.stats.new > 0 ? '#f59e0b' : 'var(--charcoal)'
                  }}>
                    {requestsStats.stats.new}
                  </p>
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.75rem', color: 'var(--mid-gray)' }}>
                    Новых
                  </span>
                </div>
                {requestsStats.stats.new > 0 && <ChevronRight size={16} style={{ color: '#f59e0b', marginLeft: 'auto' }} />}
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-s)',
                padding: 'var(--space-m)',
                backgroundColor: 'var(--cream)'
              }}>
                <Clock size={18} style={{ color: '#3b82f6' }} />
                <div>
                  <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 500 }}>
                    {requestsStats.stats.in_progress}
                  </p>
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.75rem', color: 'var(--mid-gray)' }}>
                    В работе
                  </span>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-s)',
                padding: 'var(--space-m)',
                backgroundColor: 'var(--cream)'
              }}>
                <CheckCircle size={18} style={{ color: '#22c55e' }} />
                <div>
                  <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 500 }}>
                    {requestsStats.stats.completed}
                  </p>
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.75rem', color: 'var(--mid-gray)' }}>
                    Выполнено
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div style={{
          position: 'relative',
          marginBottom: 'var(--space-xl)'
        }}>
          <Search 
            size={18} 
            style={{ 
              position: 'absolute', 
              left: '16px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: 'var(--light-gray)'
            }} 
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Поиск по имени, городу или telegram..."
            data-testid="client-search-input"
            style={{
              width: '100%',
              maxWidth: '400px',
              padding: '14px 14px 14px 48px',
              fontFamily: 'var(--font-body)',
              fontSize: '0.9375rem',
              border: '1px solid var(--soft-gray)',
              backgroundColor: 'var(--white)',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {/* Loading */}
        {loading ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px'
          }}>
            <Loader2 size={32} style={{ color: 'var(--accent-gold)', animation: 'spin 1s linear infinite' }} />
          </div>
        ) : filteredClients.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: 'var(--space-xxxl)',
            backgroundColor: 'var(--white)',
            border: '1px solid var(--soft-gray)'
          }}>
            <User size={48} style={{ color: 'var(--light-gray)', marginBottom: 'var(--space-l)' }} />
            <p style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1.5rem',
              marginBottom: 'var(--space-m)'
            }}>
              {searchQuery ? 'Клиенты не найдены' : 'Нет клиентов'}
            </p>
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.875rem',
              color: 'var(--mid-gray)'
            }}>
              {searchQuery ? 'Попробуйте изменить поисковый запрос' : 'Добавьте первого клиента'}
            </p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 'var(--space-l)'
          }}
          data-testid="clients-grid"
          >
            {filteredClients.map((client) => (
              <div
                key={client.id}
                onClick={() => navigate(`/stylist/clients/${client.id}`)}
                data-testid={`client-card-${client.id}`}
                style={{
                  backgroundColor: 'var(--white)',
                  border: '1px solid var(--soft-gray)',
                  padding: 'var(--space-xl)',
                  cursor: 'pointer',
                  transition: 'all 300ms ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 'var(--space-l)'
                }}>
                  <div>
                    <h3 style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '1.25rem',
                      fontWeight: 400,
                      marginBottom: '4px'
                    }}>
                      {client.name}
                    </h3>
                    <p style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.8125rem',
                      color: 'var(--mid-gray)'
                    }}>
                      {client.city}
                    </p>
                  </div>
                  
                  <button
                    onClick={(e) => handleGenerateMagicLink(client.id, client.name, e)}
                    title="Получить ссылку для входа"
                    style={{
                      padding: '8px',
                      backgroundColor: 'var(--cream)',
                      border: 'none',
                      cursor: 'pointer',
                      borderRadius: '4px'
                    }}
                  >
                    <LinkIcon size={16} style={{ color: 'var(--mid-gray)' }} />
                  </button>
                </div>

                {client.telegram_username && (
                  <p style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.8125rem',
                    color: 'var(--accent-gold)',
                    marginBottom: 'var(--space-m)'
                  }}>
                    {client.telegram_username}
                  </p>
                )}

                <div style={{
                  display: 'flex',
                  gap: 'var(--space-xl)',
                  paddingTop: 'var(--space-m)',
                  borderTop: '1px solid var(--soft-gray)',
                  flexWrap: 'wrap',
                  alignItems: 'center'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-s)' }}>
                    <Shirt size={16} style={{ color: 'var(--light-gray)' }} />
                    <span style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.8125rem',
                      color: 'var(--charcoal)'
                    }}>
                      {client.wardrobe_items_count} вещей
                    </span>
                  </div>
                  
                  {client.subscription ? (
                    <div style={{
                      padding: '4px 10px',
                      backgroundColor: client.subscription.remaining === 0 ? '#fef2f2' 
                        : client.subscription.remaining < 2 ? '#fffbeb' 
                        : 'rgba(143, 143, 126, 0.15)',
                      fontSize: '0.75rem',
                      fontFamily: 'var(--font-body)',
                      color: client.subscription.remaining === 0 ? '#dc2626' 
                        : client.subscription.remaining < 2 ? '#d97706' 
                        : 'var(--warm-sage)',
                      fontWeight: 500
                    }}>
                      {client.subscription.remaining < 2 && client.subscription.remaining > 0 && (
                        <AlertCircle size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
                      )}
                      {client.subscription.remaining} / {client.subscription.total} подборов
                    </div>
                  ) : (
                    <div style={{
                      padding: '4px 10px',
                      backgroundColor: '#f3f4f6',
                      fontSize: '0.75rem',
                      fontFamily: 'var(--font-body)',
                      color: 'var(--light-gray)'
                    }}>
                      Нет подписки
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* New Client Modal */}
        {showNewClientModal && (
          <NewClientModal 
            onClose={() => setShowNewClientModal(false)}
            onCreated={(newClient) => {
              setClients([...clients, newClient]);
              setShowNewClientModal(false);
            }}
          />
        )}

        {/* Magic Link Modal */}
        {magicLinkModal && (
          <div
            onClick={() => setMagicLinkModal(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 'var(--space-l)',
              zIndex: 1000
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'var(--white)',
                padding: 'var(--space-xxl)',
                maxWidth: '500px',
                width: '100%'
              }}
              data-testid="magic-link-modal"
            >
              <h3 style={{
                fontFamily: 'var(--font-display)',
                fontSize: '1.5rem',
                marginBottom: 'var(--space-m)'
              }}>
                Ссылка для {magicLinkModal.clientName}
              </h3>
              
              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem',
                color: 'var(--mid-gray)',
                marginBottom: 'var(--space-l)'
              }}>
                Скопируйте и отправьте клиенту через мессенджер
              </p>

              <div style={{
                display: 'flex',
                gap: 'var(--space-s)',
                marginBottom: 'var(--space-l)'
              }}>
                <input
                  type="text"
                  value={magicLinkModal.link}
                  readOnly
                  style={{
                    flex: 1,
                    padding: '12px',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.8125rem',
                    border: '1px solid var(--soft-gray)',
                    backgroundColor: 'var(--cream)'
                  }}
                />
                <button
                  onClick={handleCopyLink}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: copied ? '#22c55e' : 'var(--black)',
                    color: 'var(--white)',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-s)'
                  }}
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Скопировано' : 'Копировать'}
                </button>
              </div>

              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.75rem',
                color: 'var(--light-gray)'
              }}>
                Ссылка действительна 7 дней
              </p>

              <button
                onClick={() => setMagicLinkModal(null)}
                style={{
                  width: '100%',
                  marginTop: 'var(--space-l)',
                  padding: '14px',
                  backgroundColor: 'transparent',
                  color: 'var(--charcoal)',
                  border: '1px solid var(--soft-gray)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.8125rem',
                  cursor: 'pointer'
                }}
              >
                Закрыть
              </button>
            </div>
          </div>
        )}

        {/* New Client Modal */}
        {showNewClientModal && <NewClientModal onClose={() => setShowNewClientModal(false)} onCreated={() => { fetchClients(); setShowNewClientModal(false); }} />}
        
        {/* New Requests List Modal */}
        {showNewRequestsList && <NewRequestsListModal />}

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </StylistLayout>
  );
};


// New Client Modal Component
const NewClientModal = ({ onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    telegram_username: '',
    city: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.city) {
      setError('Заполните обязательные поля');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await stylistAPI.createClient({
        ...formData,
        is_active: true
      });
      onCreated(response);
    } catch (err) {
      setError('Ошибка создания клиента');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-l)',
        zIndex: 1000
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--white)',
          padding: 'var(--space-xxl)',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto'
        }}
        data-testid="new-client-modal"
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--space-xl)'
        }}>
          <h3 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.5rem'
          }}>
            Новый клиент
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 'var(--space-l)' }}>
            <label style={{
              display: 'block',
              fontFamily: 'var(--font-body)',
              fontSize: '0.75rem',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              color: 'var(--mid-gray)',
              marginBottom: 'var(--space-s)'
            }}>
              Имя *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              data-testid="new-client-name"
              style={{
                width: '100%',
                padding: '12px',
                fontFamily: 'var(--font-body)',
                fontSize: '0.9375rem',
                border: '1px solid var(--soft-gray)',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: 'var(--space-l)' }}>
            <label style={{
              display: 'block',
              fontFamily: 'var(--font-body)',
              fontSize: '0.75rem',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              color: 'var(--mid-gray)',
              marginBottom: 'var(--space-s)'
            }}>
              Город *
            </label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              data-testid="new-client-city"
              style={{
                width: '100%',
                padding: '12px',
                fontFamily: 'var(--font-body)',
                fontSize: '0.9375rem',
                border: '1px solid var(--soft-gray)',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: 'var(--space-l)' }}>
            <label style={{
              display: 'block',
              fontFamily: 'var(--font-body)',
              fontSize: '0.75rem',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              color: 'var(--mid-gray)',
              marginBottom: 'var(--space-s)'
            }}>
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              data-testid="new-client-email"
              style={{
                width: '100%',
                padding: '12px',
                fontFamily: 'var(--font-body)',
                fontSize: '0.9375rem',
                border: '1px solid var(--soft-gray)',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: 'var(--space-l)' }}>
            <label style={{
              display: 'block',
              fontFamily: 'var(--font-body)',
              fontSize: '0.75rem',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              color: 'var(--mid-gray)',
              marginBottom: 'var(--space-s)'
            }}>
              Telegram
            </label>
            <input
              type="text"
              value={formData.telegram_username}
              onChange={(e) => setFormData({ ...formData, telegram_username: e.target.value })}
              placeholder="@username"
              data-testid="new-client-telegram"
              style={{
                width: '100%',
                padding: '12px',
                fontFamily: 'var(--font-body)',
                fontSize: '0.9375rem',
                border: '1px solid var(--soft-gray)',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: 'var(--space-l)' }}>
            <label style={{
              display: 'block',
              fontFamily: 'var(--font-body)',
              fontSize: '0.75rem',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              color: 'var(--mid-gray)',
              marginBottom: 'var(--space-s)'
            }}>
              Телефон
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              data-testid="new-client-phone"
              style={{
                width: '100%',
                padding: '12px',
                fontFamily: 'var(--font-body)',
                fontSize: '0.9375rem',
                border: '1px solid var(--soft-gray)',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {error && (
            <p style={{
              color: '#ef4444',
              fontFamily: 'var(--font-body)',
              fontSize: '0.875rem',
              marginBottom: 'var(--space-l)'
            }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: 'var(--black)',
              color: 'var(--white)',
              border: 'none',
              fontFamily: 'var(--font-body)',
              fontSize: '0.8125rem',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? 'Создание...' : 'Создать клиента'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default StylistDashboard;
